import {
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  DestroyRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';

import { TsSnackbarService } from 'tsui';
import { Gallery, GalleryComponent, GalleryItem, GalleryState, ImageItem } from 'ng-gallery';
import { Account } from 'app/core/models';
import { PanelDef } from 'app/shared/components/panels/panels.types';
import { Property, Image } from 'app/modules/projects/projects.types';
import { PanelsInterfaceComponent } from '../panels-interface.component';
import { PropertiesService } from 'app/modules/properties/properties.service';
import { ConfirmService } from 'app/shared/components/widgets/dialog/confirm/confirm.service';
import { NewPermission } from '@core/enums';

@Component({
  selector: 'property-panel-images',
  templateUrl: './images.component.html',
  styleUrls: ['./images.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PanelsImagesComponent implements PanelsInterfaceComponent, OnInit {
  @Input() property: Property;
  @Input() account: Account;
  @Input() panelDef: PanelDef<any>;
  @Input() data: any;
  @Input() canEditPanelDef: boolean = true;
  @Output() onUpdate: EventEmitter<string> = new EventEmitter();
  @Output() onEditPanelDef: EventEmitter<any> = new EventEmitter();

  private readonly destroyRef = inject(DestroyRef);

  private galleryRef = this.gallery.ref('photoGallery');
  private panelName: string;
  private editMode: boolean;

  public galleryImages: GalleryItem[];
  public loading: boolean = false;
  public titleFieldDef: { name: string; label: any; type: string };
  public changeTitleMode: boolean = false;
  NewPermission = NewPermission;

  constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly propertiesService: PropertiesService,
    private readonly gallery: Gallery,
    private readonly snackBar: TsSnackbarService,
    private readonly confirmService: ConfirmService,
  ) {}

  ngOnInit() {
    this.setTitleFieldDef();
    this.fetchProperty();
  }

  private setTitleFieldDef(): void {
    this.titleFieldDef = {
      name: 'panel_name',
      label: this.panelDef.title,
      type: 'text',
    };
  }

  private fetchProperty(): void {
    this.propertiesService.property$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((property: Property) => {
      if (this.property) {
        this.property = property;
        this.loadImages();
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  private loadImages(): void {
    if (this.property) {
      this.galleryImages =
        this.property.images
          ?.filter((image: Image) => image.list_image_url && image.original_image_url)
          .map((image: Image) => new ImageItem({ src: image.original_image_url, thumb: image.list_image_url })) ?? [];

      this.galleryRef.load(this.galleryImages);
    }
  }

  public onFileSelected(event: any): void {
    const file: File = event.target.files[0];
    const allowedFormats = ['image/jpeg', 'image/png', 'image/gif'];

    if (allowedFormats.indexOf(file.type) === -1) {
      this.snackBar.open('Invalid file format. Please upload a JPEG, PNG, or GIF file.', 'error');
    } else {
      this.loading = true;
      this.propertiesService
        .uploadImage(file)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.loading = false;
          this.snackBar.open('Succesfully uploaded image.', 'success');
        });
    }
  }

  public deletePhoto(gallery: GalleryComponent): void {
    let galleryState: GalleryState;

    gallery.galleryRef.state.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((state) => {
      galleryState = state;
    });

    this.confirmService
      .openConfirmationModal({
        message: `Are you sure you want to delete the image ${
          this.property.images[galleryState.currIndex].attachment_file_name
        }?`,
      })
      .pipe(filter((result: boolean) => result))
      .subscribe(() => {
        this.propertiesService
          .deleteImage(this.property.images[galleryState.currIndex].id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => {
            gallery.reset();
            this.snackBar.open('Succesfully deleted image.', 'success');
            this.loadImages();
          });
      });

    return;
  }

  public update(params): void {
    let currentKey = Object.keys(params)[0];
    if (currentKey === 'panel_name') {
      this.panelName = params.panel_name;
    }
  }

  public saveLayout(): void {
    if (this.panelName) {
      this.panelDef.title = this.panelName;
    }
    this.onEditPanelDef.emit(this.panelDef);
    this.toggleEditMode(false);
  }

  public toggleChangeTitleMode(): void {
    this.changeTitleMode = true;
  }

  public toggleEditMode(editMode: boolean | null = null): void {
    if (editMode === null) {
      this.editMode = !this.editMode;
    } else {
      this.editMode = editMode;
    }
    this.changeTitleMode = false;
    this.changeDetectorRef.markForCheck();
  }
}
